import { ReactNode, createRef, useCallback, useMemo, useState } from "react";
import BandDeco from "./BandDeco";
import { Link, useLoaderData, useNavigate } from "react-router-dom";
import { Profile } from "../../services/profile/profileModel";
import SubmitButton from "../../components/SubmitButton";
import { profileService } from "../../services/profile/profileService";
import { useConfirmationWithIntl } from "../../components/ConfirmationDialog";
import { ReturnButton } from "../../components/ReturnButton";
import { QRCodeCanvas } from "qrcode.react";
import qrCodeBrand from "../../assets/logo-2max.png";
import { downloadURL } from "../../react-helpers/url";
import { loggerBuilder } from "../../services/logger";
import SuspenseVideoPlayer from "../../components/SuspenseVideoPlayer";
import Dialog from "../../components/Dialog";
import { cx } from "../../react-helpers/css";
import { toastsWithIntl } from "../../services/toastService";

import { thumbnailOfVideo } from "../../react-helpers/blob";
import { User } from "../../services/user/userModel";
import { useTranslation } from "react-i18next";
import useReload from "../../hooks/useReload";
import ProfileForm from "./profile/ProfileForm";
import CandidateIcon from "./CandidateIcon";
import PremiumStatsSection from "./profile/PremiumStatsSection";
import beforeStartImg from "../../assets/img/before-start.png";
import tutorialVideoFr from "../../assets/video/2mm_tutorial_new.fr.mp4";
import tutorialVideoEn from "../../assets/video/2mm_tutorial_new.en.mp4";
import tutorialVideoDe from "../../assets/video/2mm_tutorial_new.de.mp4";
import tutorialVideoEs from "../../assets/video/2mm_tutorial_new.es.mp4";

const tutorialVideosByLang: Record<string, string> = {
  fr: tutorialVideoFr,
  en: tutorialVideoEn,
  de: tutorialVideoDe,
  es: tutorialVideoEs,
};

const logger = loggerBuilder("profile-page");

const { toastSuccess } = toastsWithIntl(["candidate"]);

const ProfilePage = (): ReactNode => {
  const { t, i18n } = useTranslation();
  const { profile, video, loggedUser } = useLoaderData() as {
    profile: Profile;
    video: Promise<Blob> | null;
    loggedUser: User;
  };
  const {
    updateProfileById,
    enableProfileById,
    disableProfileById,
    deleteProfileById,
    generateProfileCoverLetter,
  } = profileService();
  const { confirm } = useConfirmationWithIntl(["candidate"]);
  const navigate = useNavigate();
  const reload = useReload();

  const [showEdit, setShowEdit] = useState(false);
  const sharedProfileUrl = useMemo(
    () => window.location.origin + "/profile-share/" + profile.uid,
    [profile.uid],
  );

  const linkedInShareURL = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(sharedProfileUrl)}`;

  const hasPremiumSubscription = useMemo(
    () =>
      !!loggedUser.subscriptionEndDate &&
      new Date(loggedUser.subscriptionEndDate) > new Date() &&
      loggedUser.subscriptionStartDate !== null,
    [loggedUser],
  );

  const ref = createRef<HTMLVideoElement>();

  const updateThumbnail = useCallback(
    async (video: HTMLVideoElement) => {
      const newThumbnail = await thumbnailOfVideo(video);

      return updateProfileById(profile.id, { thumbnail: newThumbnail });
    },
    [profile.id, updateProfileById],
  );

  const [showThumbnailPopup, setShowThumbnailPopup] = useState(false);
  const [showHelpVideoPopup, setShowHelpVideoPopup] = useState(false);

  const tutorialVideoSrc =
    tutorialVideosByLang[i18n.language.split("-")[0]] ?? tutorialVideoFr;

  const downloadCoverLetter = useCallback(
    (qrCode: HTMLCanvasElement) => {
      // if the cover letter is not generated yet, we generate it
      const qrCodeData = qrCode.toDataURL("image/jpeg");
      return generateProfileCoverLetter(profile.id, qrCodeData).then(
        (coverLetter) => {
          const url = URL.createObjectURL(
            new Blob([coverLetter], { type: "application/pdf" }),
          );
          const firstname = loggedUser.firstname.trim();
          downloadURL(
            url,
            `${loggedUser.lastname.trim().toUpperCase()}_${
              firstname.charAt(0).toUpperCase() +
              firstname.slice(1).toLowerCase()
            }_2'MAX.pdf`,
          );
          reload();
        },
      );
    },
    [profile.id, generateProfileCoverLetter, reload, loggedUser],
  );

  return (
    <div className="layout_content cand-page">
      <section className="ui-band cand-band">
        <BandDeco />
        <div className="ui-band_in cand-band_in">
          <ReturnButton forcedUrl="/candidate">
            {t("common:sidebar.HOME")}
          </ReturnButton>
          <div className="cand-band_row">
            <div>
              <h1>{profile.name}</h1>
              <div className="cand-band_chips">
                {profile.featured && (
                  <span className="ui-chip --nod">
                    {t("candidate:profile.MAIN_PROFILE")}
                  </span>
                )}
                <span className="ui-chip --nod">
                  {profile.disabledDate
                    ? t("candidate:profile.PROFILE_DISABLED")
                    : t("candidate:profile.PROFILE_ACTIVE")}
                </span>
                {!profile.disabledDate && !profile.featured && (
                  <SubmitButton
                    className="btn--2 --btn--s"
                    onClick={() =>
                      updateProfileById(profile.id, { featured: true }).then(
                        () => reload(),
                      )
                    }
                  >
                    {t("candidate:profile.buttons.SET_AS_MAIN_PROFILE")}
                  </SubmitButton>
                )}
              </div>
            </div>
            <div className="cand-band_actions">
              <SubmitButton
                className="cand-iconbtn"
                onClick={() =>
                  profile.disabledDate
                    ? enableProfileById(profile.id).then(() => reload())
                    : confirm(
                        "candidate:disable-profile.CONFIRM",
                        () =>
                          disableProfileById(profile.id).then(() => reload()),
                        true,
                      )
                }
                title={t("candidate:profile.buttons.tips.ACTIVATION", {
                  action: profile.disabledDate
                    ? t("candidate:profile.buttons.ENABLE")
                    : t("candidate:profile.buttons.DISABLE"),
                })}
              >
                <CandidateIcon
                  name={profile.disabledDate ? "unlock" : "lock"}
                />
              </SubmitButton>
              <button
                className="cand-iconbtn"
                onClick={() => setShowEdit(true)}
                title={t("candidate:profile.buttons.tips.EDIT")}
              >
                <CandidateIcon name="edit" />
              </button>

              <SubmitButton
                className="cand-iconbtn"
                onClick={() =>
                  confirm(
                    "candidate:delete-profile.CONFIRM",
                    () =>
                      deleteProfileById(profile.id).then(() => {
                        navigate("..");
                        reload();
                      }),
                    true,
                  )
                }
                title={t("candidate:profile.buttons.tips.DELETE")}
              >
                <CandidateIcon name="trash" />
              </SubmitButton>
            </div>
          </div>
        </div>
      </section>

      <div className="ui-page cand-profile">
        {!profile.disabledDate && !video && (
          <div className="cand-help">
            <div className="ui-banner">
              {/* « Vous pourrez : … » est passé sur le tableau de bord (Nicolas, 31/10). */}
              <div className="ui-banner_grow">
                <p>{t("candidate:profile.FIRST_TAKE")}</p>
                <p>{t("candidate:profile.FIRST_TAKE_THEN")}</p>
              </div>
            </div>
            <div className="help-block">
              <div className="help-block_text">
                <strong className="help-block_title">
                  {t("candidate:profile.help.TITLE")}
                </strong>
                <span className="help-block_subtitle">
                  {t("candidate:profile.help.SUBTITLE")}
                </span>
              </div>
              <button
                type="button"
                className="help-block_thumbnail"
                onClick={() => setShowHelpVideoPopup(true)}
                title={t("candidate:profile.help.SUBTITLE")}
              >
                <img src={beforeStartImg} alt="" />
              </button>
            </div>
          </div>
        )}

        <div
          className={cx([
            "cand-profile_grid",
            (!video || !!profile.disabledDate) && "--single",
          ])}
        >
          <div className="cand-col">
            {video && (
              <div className="cand-profile_actions">
                <Link className="btn--2 --btn--s" to={"record"}>
                  <CandidateIcon name="film" />
                  {t("candidate:profile.buttons.RETAKE_VIDEO")}
                </Link>
                <button
                  className="btn--2 --outlined --btn--s"
                  onClick={() => setShowThumbnailPopup(true)}
                >
                  <CandidateIcon name="image" />
                  {t("candidate:profile.buttons.UPDATE_THUMBNAIL")}
                </button>
              </div>
            )}

            {video && (
              <div
                className={cx([
                  "video-container cand-player",
                  !!profile.disabledDate && "--off",
                ])}
              >
                <SuspenseVideoPlayer
                  videoPromise={video}
                  thumbnail={profile.thumbnail}
                />
              </div>
            )}

            {!profile.disabledDate && !video && (
              <div className="video-placeholder cand-player">
                <canvas width="16" height="9" />
                <div className="video-placeholder_actions">
                  <Link to={"record"} className="btn --btn--l">
                    {t("candidate:profile.buttons.RECORD_VIDEO")}
                  </Link>
                </div>
              </div>
            )}

            {!!video && !profile.disabledDate && (
              <PremiumStatsSection
                loggedUser={loggedUser}
                isPremium={hasPremiumSubscription}
                consultations={profile.userVisiting ?? []}
              />
            )}
          </div>

          {!!video && !profile.disabledDate && (
            <aside className="ui-card --pad cand-sidebar">
              <h2 className="ui-st cand-share_title">
                <CandidateIcon name="share" />
                {t("candidate:share.SHARE_TITLE")}
              </h2>
              <div className="cand-share-list">
                <button
                  className="cand-share"
                  onClick={() => {
                    void navigator.clipboard
                      .writeText(sharedProfileUrl)
                      .then(() => {
                        toastSuccess("candidate:share.LINK_COPIED");
                      });
                  }}
                >
                  <span className="cand-share_ic">
                    <CandidateIcon name="copy" />
                  </span>
                  <span className="cand-share_tx">
                    <b>{t("candidate:profile.buttons.share.LINK")}</b>
                    <span>
                      {t("candidate:profile.buttons.tips.SHARE_LINK")}
                    </span>
                  </span>
                </button>
                <button
                  className="cand-share"
                  onClick={() => {
                    const qrRef = document.getElementById(
                      "qrcode",
                    ) as HTMLCanvasElement | null;
                    if (qrRef) {
                      void downloadCoverLetter(qrRef);
                    } else {
                      logger.warn("qr code not found");
                    }
                  }}
                >
                  <span className="cand-share_ic">
                    <CandidateIcon name="file" />
                  </span>
                  <span className="cand-share_tx">
                    <b>{t("candidate:profile.buttons.share.PDF_2MAX")}</b>
                    <span>{t("candidate:profile.buttons.tips.SHARE_PDF")}</span>
                  </span>
                </button>
                <a
                  className="cand-share --linkedin"
                  href={linkedInShareURL}
                  target="_blank"
                  rel="noreferrer"
                >
                  <span className="cand-share_ic">
                    <CandidateIcon name="linkedin" />
                  </span>
                  <span className="cand-share_tx">
                    <b>{t("candidate:profile.buttons.share.LINKEDIN")}</b>
                    <span>
                      {t("candidate:profile.buttons.tips.SHARE_LINKEDIN")}
                    </span>
                  </span>
                </a>

                <button
                  className="cand-share"
                  onClick={() => {
                    const qrRef = document.getElementById(
                      "qrcode",
                    ) as HTMLCanvasElement | null;
                    if (qrRef) {
                      downloadURL(qrRef.toDataURL("image/png"), "qrcode.png");
                    } else {
                      logger.warn("qr code not found");
                    }
                  }}
                >
                  <span className="cand-share_ic">
                    <CandidateIcon name="qr" />
                  </span>
                  <span className="cand-share_tx">
                    <b>{t("candidate:profile.buttons.share.QR_CODE_FOR_CV")}</b>
                    <span>
                      {t("candidate:profile.buttons.tips.SHARE_QR_CODE")}
                    </span>
                  </span>
                </button>
              </div>
              <div className="cand-qr">
                <QRCodeCanvas
                  id={"qrcode"}
                  includeMargin
                  value={
                    window.location.origin + "/profile-share/" + profile.uid
                  }
                  size={150}
                  level="H"
                  imageSettings={{
                    src: qrCodeBrand,
                    height: 50,
                    width: 50,
                    excavate: true,
                  }}
                  fgColor="#0c0557"
                />
                <p className="ui-small ui-muted">
                  {t("candidate:share.QR_HELP")}
                </p>
              </div>
            </aside>
          )}
        </div>
      </div>
      {showEdit && (
        <Dialog className="--branded" onClose={() => setShowEdit(false)}>
          <>
            <div className="popup_head">
              <h2 className="popup_title">
                {t("candidate:update-profile.DIALOG_TITLE")}
              </h2>
            </div>

            <ProfileForm
              onSubmit={(values) => {
                return updateProfileById(profile.id, values).then(() => {
                  setShowEdit(false);
                  reload();
                });
              }}
              onCancel={() => setShowEdit(false)}
              profile={profile}
            />
          </>
        </Dialog>
      )}
      {showHelpVideoPopup && (
        <Dialog
          className="--branded popup--help-video"
          onClose={() => setShowHelpVideoPopup(false)}
        >
          <>
            <div className="popup_head">
              <h2 className="popup_title">
                {t("candidate:profile.help.POPUP_TITLE")}
              </h2>
            </div>
            <div className="popup_body">
              <video
                className="videoplayer"
                playsInline
                src={tutorialVideoSrc}
                controls
                autoPlay
                onContextMenu={(e) => e.preventDefault()}
              />
            </div>
          </>
        </Dialog>
      )}
      {video && showThumbnailPopup && (
        <Dialog
          className="popup--update-thumbnail"
          onClose={() => setShowThumbnailPopup(false)}
        >
          <>
            <div className="popup_head">
              <h2 className="popup_title">
                {t("candidate:profile.thumbnail.DIALOG_TITLE")}
              </h2>
            </div>
            <div className="popup_body">
              <div className="explanations-row">
                <p className="body--40">
                  {t("candidate:profile.thumbnail.THUMBNAIL_EXPLANATION")}
                </p>
              </div>

              <div className="lblock">
                <SuspenseVideoPlayer
                  videoPromise={video}
                  ref={ref}
                  thumbnail={profile.thumbnail}
                />
              </div>
            </div>
            <div className="popup_footer">
              <button
                className="btn--grey"
                onClick={() => setShowThumbnailPopup(false)}
              >
                {t("candidate:profile.buttons.CANCEL")}
              </button>
              <SubmitButton
                className="btn"
                onClick={() => {
                  return updateThumbnail(ref.current!).then(() => reload());
                }}
              >
                {t("candidate:profile.buttons.UPDATE")}
              </SubmitButton>
            </div>
          </>
        </Dialog>
      )}
    </div>
  );
};

export default ProfilePage;
